import React, { useState, useMemo, useCallback } from "react";
import {
  Typography,
  MenuItem,
  FormControl,
  TextField,
  Select,
  Grid,
  Box,
  InputAdornment,
  Tooltip,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { BuyerListingOptionsProvider } from "../../../../buyerListing/buyerListingOptions/buyerListingOptionsProvider";
import { noneOption, removeMethodOfContactOption } from "./StyledComponents";
import { IHomesite } from "../../../../../../api/homesites/homesites.types";
import { HomesiteColDef } from "../../../../../../components/core/homesites/homesiteColumns";
import { EditedValues } from "./AdditionalHomeSiteDetails";
import { formatCurrency } from "../../../../../../utils/functions";
import { userInputErrorComponent } from "../../../../../../components/error/userInputError";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { DateOrTimeView, DateView } from "@mui/x-date-pickers/models/views";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ClearIcon from "@mui/icons-material/Clear";
import { INVALID_DATE, INVALID_FUTURE_DATE } from "../../../../../../utils/common/validator";
import {
  DELAY_REASON_OPTIONS,
  DelayReasonOption,
  OTHER_REASON_MAX_LENGTH,
} from "../../../../buyerListing/RevisedEcoeDateModal";


const menuProps = {
  PaperProps: { sx: { maxHeight: 300, "& .MuiMenuItem-root": { height: 36 } } },
};

/** Returns true if the given date string/Date is strictly after today (midnight local). */
function isFutureFromToday(value: unknown): boolean {
  if (value == null || value === "") return false;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else {
    const str = String(value);
    d = /^\d{4}-\d{2}-\d{2}$/.test(str) ? new Date(str + "T12:00:00") : new Date(str);
  }
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dCopy = new Date(d.getTime());
  dCopy.setHours(0, 0, 0, 0);
  return dCopy.getTime() > today.getTime();
}

/** Parse a saved delayedReason string into { reason, otherText }. Handles all reasons with optional text. */
function parseDelayedReason(value: string): { reason: DelayReasonOption | null; otherText: string } {
  if (!value) return { reason: null, otherText: "" };
  
  // Check if it has a suffix pattern: "Reason - additional text"
  const match = DELAY_REASON_OPTIONS.find((opt) => {
    return value === opt || value.startsWith(opt + " - ");
  });
  
  if (match) {
    const otherText = value.startsWith(match + " - ") ? value.slice(match.length + 3) : "";
    return { reason: match, otherText };
  }
  
  return { reason: null, otherText: "" };
}

interface AdditionalHomeSiteDetailsFormProps {
  fields: HomesiteColDef[];
  editedFields: Record<string, EditedValues>;
  homesite: IHomesite;
  onChange: (field: HomesiteColDef, value: any) => void;
}

const AdditionalHomeSiteDetailsForm: React.FC<
  AdditionalHomeSiteDetailsFormProps
> = ({ fields, editedFields, homesite, onChange }) => {
  const [isEditing, setEdit] = useState(false);
  const [dtOpen, setDtOpen] = useState<{[key: string]: boolean}>({});
  const [error, setError] = useState<{[key: string]: boolean}>({});
  const [errorText, setErrorText] = useState<{[key: string]: string}>({});
  const [key, setKey] = useState("pickerKey");

  // ── Revised ECOE / Delayed Reason inline state ──────────────────────────
  const initialDelayedReason = editedFields["delayedReason"]?.value ?? homesite.delayedReason ?? "";
  const initialParsed = parseDelayedReason(String(initialDelayedReason));
  const [inlineReason, setInlineReason] = useState<DelayReasonOption | null>(initialParsed.reason);
  const [inlineOtherText, setInlineOtherText] = useState(initialParsed.otherText);

  /** True when the user has just picked a future revisedEcoe in this session. */
  const showInlineDelayedReason =
    "revisedEcoe" in editedFields && isFutureFromToday(editedFields["revisedEcoe"]?.value);

  /**
   * Minimal field def for delayedReason — used when calling onChange so the
   * parent's handleFieldChange knows how to diff against the original value.
   */
  const delayedReasonFieldDef = useMemo(
    () =>
      fields.find((f) => f.field === "delayedReason") ??
      ({
        field: "delayedReason",
        type: "string",
        valueGetter: (_: unknown, row: IHomesite) => row?.delayedReason ?? "",
      } as unknown as HomesiteColDef),
    [fields]
  );

  const handleInlineReasonChange = useCallback(
    (newReason: DelayReasonOption | null) => {
      const isReasonChanged = newReason !== inlineReason;
      const nextOtherText = isReasonChanged ? "" : inlineOtherText;

      setInlineReason(newReason);
      if (isReasonChanged) {
        setInlineOtherText("");
      }

      if (!newReason) {
        onChange(delayedReasonFieldDef, "");
        setInlineOtherText("");
        } else if (newReason === "Other") {
          // Keep value empty until required details are entered.
        const val = nextOtherText.trim() ? `Other - ${nextOtherText.trim()}` : "";
          onChange(delayedReasonFieldDef, val);
      } else if (nextOtherText.trim()) {
        // If there's text, save it with the reason
        onChange(delayedReasonFieldDef, `${newReason} - ${nextOtherText.trim()}`);
      } else {
        // No text, just save the reason
        onChange(delayedReasonFieldDef, newReason);
      }
    },
    [onChange, delayedReasonFieldDef, inlineOtherText, inlineReason]
  );

  const handleInlineOtherTextChange = useCallback(
    (text: string) => {
      setInlineOtherText(text);
      if (!inlineReason) return;
      
      // For "Other", text is mandatory; for others, it's optional
      if (inlineReason === "Other") {
        // Only save if there's text
        const val = text.trim() ? `${inlineReason} - ${text.trim()}` : "";
        onChange(delayedReasonFieldDef, val);
      } else {
        // Optional text for other reasons
        const val = text.trim() ? `${inlineReason} - ${text.trim()}` : inlineReason;
        onChange(delayedReasonFieldDef, val);
      }
    },
    [onChange, delayedReasonFieldDef, inlineReason]
  );
  const isCurrencyField = (field: HomesiteColDef) => {
    return field.valueFormatter === formatCurrency;
  };

  const options = (field: HomesiteColDef) => {
    return BuyerListingOptionsProvider.getOptionsByField(field.field) || [];
  };

  const handleError = (error: string | null, field: string) => {
    switch (error) {
      case "invalidDate":
        setError((prev) => ({ ...prev, [field]: true }));
        setErrorText((prev) => ({ ...prev, [field]: INVALID_DATE }));
        break;
      case "disableFuture":
        setError((prev) => ({ ...prev, [field]: true }));
        setErrorText((prev) => ({ ...prev, [field]: INVALID_FUTURE_DATE }));
        break;
      case null:
        setError((prev) => ({ ...prev, [field]: false }));
        setErrorText((prev) => ({ ...prev, [field]: "" }));
        break;
      default:
        console.error("Unknown error type:", error);
        setError((prev) => ({ ...prev, [field]: false }));
        setErrorText((prev) => ({ ...prev, [field]: "" }));
    }
  };
  
  const handleKeyEvent = (event: React.KeyboardEvent<HTMLInputElement>, field: HomesiteColDef, value: string, format: string) => {
    if (event.key === "Enter" || event.key === "Tab") {
      const raw = (event.target as HTMLInputElement).value.trim();
      if (raw === format || raw === "") {
        onChange(field, "");
        if (field.field === "revisedEcoe") {
          onChange(delayedReasonFieldDef, "");
          setInlineReason(null);
          setInlineOtherText("");
        }
        return;
      }
      const eValue = dayjs(raw);
      if (eValue.isValid() && !error[field.field]) {
        onChange(field, eValue.format(format));
      } else {
        onChange(field, value);
      }
    } else if (event.key === "Escape") {
      clearError(field.field);
      setKey(`pickerKey-${Date.now()}`); // Force re-render
      onChange(field, value);
    }
  };

  const clearError = (field: string) => {
    setError((prev) => ({ ...prev, [field]: false }));
    setErrorText((prev) => ({ ...prev, [field]: "" }));
  };

  const handleOnBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    field: HomesiteColDef,
    value: string,
    displayFormat: string,
    saveFormat: string,
  ) => {
    
    const raw = event.target.value.trim();
    if (raw === displayFormat || raw === "") {
      onChange(field, "");
      if (field.field === "revisedEcoe") {
        onChange(delayedReasonFieldDef, "");
        setInlineReason(null);
        setInlineOtherText("");
      }
      return;
    }
    const eValue = dayjs(raw);
    
    if (eValue && eValue.isValid() && !error[field.field]) {
      onChange(field, eValue.format(saveFormat));
    } else {
      clearError(field.field);    
      setKey(`pickerKey-${Date.now()}`); // Force re-render
      onChange(field, value);
    }
  }

  const handleDtOpen = (field: string) => {
    setDtOpen((prev) => ({ ...prev, [field]: true }));
  }

  const handleDtClose = (field: string) => {
    setDtOpen((prev) => ({ ...prev, [field]: false }));
  }

  const isDtOpen = (field: string) => {
    return dtOpen[field] || false;
  }

  const datePickerViews: readonly DateView[] = [
      "year",
      "month",
      "day",
    ];

  const dateTimePickerViews: readonly DateOrTimeView[] = [
    "year",
    "month",
    "day",
    "hours",
    "minutes",
  ];

  return (
    <Box
      sx={{
        width: "988px",
        height: "480px",
        padding: "16px",
        gap: "2px",
        borderRadius: "4px 0px 0px 0px",
        backgroundColor: "#FFFFFF",
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
        "-ms-overflow-style": "auto",
        "scrollbar-width": "auto",
      }}
    >
      <Grid container spacing={2}>
        {fields.map((field, index) => {
          const value = editedFields[field.field]
            ? editedFields[field.field].value
            : field.valueGetter?.(null, homesite);
          
          const renderField = () => {
            /**
             * Special case: when revisedEcoe is edited to a future date,
             * delayedReason switches from read-only display to an editable
             * Autocomplete IN ITS OWN GRID ITEM so the layout stays intact.
             */
            if (field.field === "delayedReason" && showInlineDelayedReason) {
              return (
                <Box sx={{ width: "235px" }}>
                  <Autocomplete
                    size="small"
                    options={[...DELAY_REASON_OPTIONS]}
                    value={inlineReason}
                    onChange={(_, newReason) =>
                      handleInlineReasonChange(newReason as DelayReasonOption | null)
                    }
                    renderInput={(rParams) => (
                      <TextField
                        {...rParams}
                        placeholder="Select reason..."
                        size="small"
                        variant="standard"
                        sx={{
                          width: "235px",
                          "& .MuiInputBase-root": { backgroundColor: "#F4F6F9" },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option} sx={{ py: 0.5 }}>
                        <Chip label={option} size="small" variant="outlined" />
                      </Box>
                    )}
                    sx={{ width: "235px" }}
                  />
                  {inlineReason && (
                    <TextField
                      autoFocus
                      size="small"
                      variant="standard"
                      placeholder={inlineReason === "Other" ? "Add delay reason..." : "Add additional details (optional)"}
                      value={inlineOtherText}
                      onChange={(e) => handleInlineOtherTextChange(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      multiline
                      maxRows={3}
                      inputProps={{ maxLength: OTHER_REASON_MAX_LENGTH }}
                      error={inlineReason === "Other" && !inlineOtherText.trim()}
                      helperText={
                        inlineReason === "Other" && !inlineOtherText.trim()
                          ? "Required — enter a reason"
                          : undefined
                      }
                      sx={{
                        mt: 1,
                        width: "235px",
                        "& .MuiInputBase-root": { backgroundColor: "#F4F6F9" },
                      }}
                    />
                  )}
                </Box>
              );
            }

            if (!field.editable) {
              return (
                <TextField
                  sx={{
                    width: "235px",
                    height: "36px",
                    "& .MuiInputBase-root": {
                      alignItems: "center",
                      backgroundColor: "#F4F6F9",
                      paddingLeft: "8px",
                    },
                  }}
                  variant="standard"
                  value={
                    field.valueFormatter ? field.valueFormatter(value) : value
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                />
              );
            } else {
              const originalValue =
                field.valueGetter && field.valueFormatter
                  ? field.valueFormatter(field.valueGetter(null, homesite))
                  : undefined;
              const validField =
                field.valueValidator && !!editedFields[field.field]
                  ? field.valueValidator(editedFields[field.field].value)
                  : undefined;
              const hasChanged =
                value !== originalValue && !!editedFields[field.field];

              switch (field.type) {
                case "date": {
                  const datePicker = (
                    <DatePicker
                      key={key}
                      views={datePickerViews}
                      value={value && dayjs(value).isValid() ? dayjs(value) : null}
                      autoFocus
                      onAccept={(accepted: Dayjs | null) => {
                        const dateStr = accepted?.format("YYYY-MM-DD") || "";
                        onChange(field, dateStr);
                        if (field.field === "revisedEcoe" && !isFutureFromToday(dateStr)) {
                          // Clear delayed reason when past/today date is selected
                          onChange(delayedReasonFieldDef, "");
                          setInlineReason(null);
                          setInlineOtherText("");
                        }
                      }}
                      onChange={(accepted: Dayjs | null) => {
                        if (accepted && accepted.isValid()) {
                          onChange(field, accepted.format("YYYY-MM-DD"));
                        }
                      }}
                      onError={(err) => { handleError(err, field.field); }}
                      slotProps={{
                        desktopTrapFocus: { disableEnforceFocus: true },
                        textField: {
                          variant: "standard",
                          sx: {
                            width: "235px",
                            height: "36px",
                            "& .MuiInputBase-root": {
                              alignItems: "center",
                              backgroundColor: hasChanged ? "#D0E8FF" : "#F4F6F9",
                            },
                          },
                          error: error[field.field],
                          helperText: !!errorText[field.field] ? errorText[field.field] : "",
                          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                            handleOnBlur(event, field, value, "MM-DD-YYYY", "YYYY-MM-DD");
                          },
                          onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            handleKeyEvent(event, field, value, "YYYY-MM-DD");
                          },
                        },
                        actionBar: { actions: ["clear"] },
                      }}
                      format="MM-DD-YYYY"
                      disableFuture={field.field === "lastContact"}
                    />
                  );

                  return datePicker;
                }
                case "dateTime": {
                  return (
                    <DateTimePicker
                      key={key}
                      views={dateTimePickerViews}
                      value={value && dayjs(value).isValid() ? dayjs(value) : null}
                      autoFocus
                      open={isDtOpen(field.field)}
                      onOpen={() => handleDtOpen(field.field)}
                      onAccept={ (value: Dayjs | null) => {
                        onChange(field, value?.format("YYYY-MM-DD HH:mm") || "");
                      }}
                      onChange={(value: Dayjs | null) => {
                        if (value && value.isValid()) {
                          onChange(field, value.format("YYYY-MM-DD HH:mm"));
                        }
                      }}
                      onClose={ () => {
                        handleDtClose(field.field);
                      }}
                      onError= {(error) => { handleError(error, field.field); }}
                      format="MM-DD-YYYY HH:mm"
                      ampm={false}
                      timeSteps={{ minutes: 15 }}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          sx: {
                            width: "235px",
                            height: "36px",
                            "& .MuiInputBase-root": {
                              alignItems: "center",
                              backgroundColor: hasChanged ? "#D0E8FF" : "#F4F6F9",
                            },
                          },
                          error: error[field.field],
                          helperText: !!errorText[field.field] ? errorText[field.field] : "",
                          inputProps: { spellCheck: false },
                          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                            handleOnBlur(event, field, value, "MM-DD-YYYY hh:mm", "YYYY-MM-DD HH:mm");
                          },
                          onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            handleKeyEvent(event, field, value, "YYYY-MM-DD HH:mm");
                          },                              
                        },
                        actionBar: { actions: ["clear", "accept"],
                          onClear: () => {
                            onChange(field, "");
                            handleDtClose(field.field);
                          }
                        },
                      }}
                      closeOnSelect={false}

                    />
                  );
                }
                case "singleSelect":
                  return (
                    <FormControl variant="standard">
                      {
                        <Select
                          value={value ? value : ""}
                          onChange={(e) => onChange(field, e.target.value)}
                          MenuProps={menuProps}
                          disabled={false}
                          sx={{
                            width: "235px",
                            backgroundColor: "#F4F6F9",
                            "& .MuiInputBase-root": {
                              alignItems: "center",
                              backgroundColor: !hasChanged
                                ? "#D0E8FF"
                                : "#F4F6F9", // Subtle blue background for edited fields
                            },
                          }}
                        >
                          {options(field).map((option: any) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              disabled={
                                option.label === removeMethodOfContactOption
                                  ? editedFields[field.field] &&
                                    editedFields[field.field].value === ""
                                  : option.label === noneOption
                                    ? editedFields[field.field] &&
                                      editedFields[field.field].value === ""
                                    : false
                              }
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      }
                    </FormControl>
                  );
                default:
                  return (
                    <TextField
                      sx={{
                        width: "235px",
                        height: "36px",
                        "& .MuiInputBase-root": {
                          alignItems: "center",
                          backgroundColor: hasChanged ? "#D0E8FF" : "#F4F6F9", // Subtle blue background for edited fields
                          paddingLeft: "8px",
                        },
                      }}
                      // Not edit mode and value has changed and is valid
                      error={!isEditing && validField && !validField.isValid}
                      helperText={
                        (!isEditing &&
                        validField &&
                        !validField.isValid) ?
                        userInputErrorComponent(validField.errorMessage!) : ""
                      }
                      variant="standard"
                      value={value ? value : ""}
                      InputProps={{
                        readOnly: !field.editable,
                        startAdornment:
                          value && isCurrencyField(field) ? (
                            <InputAdornment position="start">$</InputAdornment>
                          ) : undefined,
                        endAdornment:
                          value && field.editable ? (
                            <InputAdornment position="end">
                              <Tooltip title="Clear" arrow placement="top">
                                <IconButton onClick={() => onChange(field, "")}>
                                  <ClearIcon
                                    style={{
                                      fontSize: "12px",
                                      stroke: "black",
                                      strokeWidth: 1.5,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ) : undefined,
                      }}
                      disabled={!field.editable}
                      onClick={() => {
                        setEdit(true);
                      }}
                      // We have onBlur valdiation here.
                      onBlur={(e) => {
                        let finalValue = e.target.value.trim();
                        onChange(field, finalValue);
                        setEdit(false);
                      }}
                      onChange={(e) => {
                        onChange(field, e.target.value);
                      }}
                    />
                  );
              }
            }
          };

          const isDelayedReasonRequired =
            field.field === "delayedReason" && showInlineDelayedReason;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  color: isDelayedReasonRequired
                    ? "error.main"
                    : field.editable
                    ? "inherit"
                    : "#9FB1BD",
                  fontWeight: isDelayedReasonRequired ? 500 : "inherit",
                }}
              >
                {field.headerName}
                {isDelayedReasonRequired && " *"}
                {field.editable && !isDelayedReasonRequired && (
                  <EditOutlinedIcon
                    fontSize="small"
                    sx={{ ml: 1, color: "#1F75BB", verticalAlign: "middle" }}
                  />
                )}
              </Typography>
              {renderField()}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AdditionalHomeSiteDetailsForm;
